/* Administración del módulo de Eventos.
 *
 * Solo accesible para administradores del módulo (validado por backend con la
 * tabla AE_Evento_Admin). Permite:
 *  - Crear / editar eventos (con fechas, configuración y preguntas de evaluación).
 *  - Publicar / cancelar.
 *  - Seleccionar invitados (marcando "reconocido" -> auto activa 1 acompañante)
 *    y definir cuántos acompañantes puede llevar cada uno.
 *  - Designar tomadores de asistencia.
 *  - Ver confirmados y presentes.
 */
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTextarea,
  IonToggle,
  IonHeader,
  IonToolbar,
  IonTitle,
  useIonToast,
} from "@ionic/react";
import { trashOutline } from "ionicons/icons";
import { TextField, Typography } from "@material-ui/core";
import { DatePicker, TimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import Header from "src/components/Header";
import EventoPresentesLista from "src/components/EventoPresentesLista";
import Eventos, { EventoDetalle, EventoItem, Pregunta, Segmento, UsuarioActivo } from "src/services/eventos";
import { exportarExcel, ColumnaExcel } from "src/utils/exportarExcel";

// Campos de fecha/hora del evento (mismo orden que se muestran).
const CAMPOS_FECHA: Array<{ campo: string; label: string }> = [
  { campo: "fecha_inicio_invitacion", label: "Inicio de invitación" },
  { campo: "fecha_fin_confirmacion", label: "Fin de confirmación" },
  { campo: "fecha_inicio_evento", label: "Inicio del evento" },
  { campo: "fecha_fin_evento", label: "Fin del evento" },
];

type Vista = "lista" | "form" | "detalle";

const formVacio = () => ({
  id: 0,
  tema: "",
  descripcion: "",
  lugar: "",
  fecha_inicio_invitacion: null as Moment | null,
  fecha_fin_confirmacion: null as Moment | null,
  fecha_inicio_evento: null as Moment | null,
  fecha_fin_evento: null as Moment | null,
  invitados_por_defecto: 1,
  requiere_evaluacion: false,
  requiere_segmentacion: false,
  permite_acompanantes: true,
  requiere_invitacion: true,
  respuesta_obligatoria: false,
  solo_administrativos: false,
  requiere_pedido: false,
  requiere_firma: false,
  requiere_qr: false,
  permite_cuotas: false,
  max_cuotas: null as number | null,
  recordatorio_horas_antes: 3 as number | null,
  preguntas: [] as Pregunta[],
  segmentos: [] as Segmento[],
  items: [] as EventoItem[],
});

// Formato de montos (guaraníes, sin decimales).
const fmtMonto = (n: number | null | undefined) =>
  `Gs. ${Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 0 })}`;

// Parsea 'YYYY-MM-DD HH:MM:SS' (backend) a Moment.
const aMoment = (iso?: string): Moment | null =>
  iso ? moment(String(iso).replace(" ", "T"), "YYYY-MM-DDTHH:mm:ss") : null;

// Formatea Moment a ISO 8601 con 'T' para el backend.
const aISO = (m: Moment | null): string => (m ? m.format("YYYY-MM-DDTHH:mm:ss") : "");

// Filtro por nombre o cédula (case-insensitive).
const coincide = (item: any, q: string): boolean => {
  const s = (q || "").trim().toLowerCase();
  if (!s) return true;
  return (
    String(item?.nombre || "").toLowerCase().includes(s) ||
    String(item?.cedula || "").toLowerCase().includes(s)
  );
};

const EventosAdmin: React.FC = () => {
  const [present] = useIonToast();
  const [esAdmin, setEsAdmin] = useState<boolean | null>(null);
  const [vista, setVista] = useState<Vista>("lista");
  const [cargando, setCargando] = useState(true);
  const [eventos, setEventos] = useState<any[]>([]);
  const [form, setForm] = useState(formVacio());
  const [guardando, setGuardando] = useState(false);

  // Detalle
  const [detalle, setDetalle] = useState<EventoDetalle | null>(null);
  const [invitados, setInvitados] = useState<any[]>([]);
  const [confirmados, setConfirmados] = useState<any[]>([]);
  const [presentes, setPresentes] = useState<any[]>([]);
  const [tabDetalle, setTabDetalle] = useState<"invitados" | "tomadores" | "confirmados" | "rechazados" | "presentes" | "pedidos">("invitados");
  const [rechazados, setRechazados] = useState<any[]>([]);
  const [pedidosInfo, setPedidosInfo] = useState<Awaited<ReturnType<typeof Eventos.pedidosAdmin>> | null>(null);
  const [busqLista, setBusqLista] = useState("");
  const [filtroSegPres, setFiltroSegPres] = useState(""); // "" = todos los segmentos
  const [exportando, setExportando] = useState(false);

  // Selector de invitados
  const [showInvitados, setShowInvitados] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [usuarios, setUsuarios] = useState<UsuarioActivo[]>([]);
  // mapa cedula -> { nombre, cargo, es_reconocido, max_acompanantes }
  const [seleccion, setSeleccion] = useState<Record<string, any>>({});

  // Selector de tomadores de asistencia (entre los invitados del evento)
  const [showTomadores, setShowTomadores] = useState(false);
  const [selTomadores, setSelTomadores] = useState<Record<string, boolean>>({});
  const [busquedaTomadores, setBusquedaTomadores] = useState("");

  useEffect(() => {
    (async () => {
      const admin = await Eventos.esAdmin();
      setEsAdmin(admin);
      if (admin) await cargarLista();
      else setCargando(false);
    })();
  }, []);

  const cargarLista = async () => {
    setCargando(true);
    try {
      setEventos(await Eventos.listarEventos());
    } finally {
      setCargando(false);
    }
  };

  // -------------------- Formulario crear/editar --------------------
  const nuevoEvento = () => {
    setForm(formVacio());
    setVista("form");
  };

  const editarEvento = async (id: number) => {
    const d = await Eventos.obtenerEvento(id);
    if (!d) return;
    setForm({
      id: d.id,
      tema: d.tema || "",
      descripcion: d.descripcion || "",
      lugar: d.lugar || "",
      fecha_inicio_invitacion: aMoment(d.fecha_inicio_invitacion),
      fecha_fin_confirmacion: aMoment(d.fecha_fin_confirmacion),
      fecha_inicio_evento: aMoment(d.fecha_inicio_evento),
      fecha_fin_evento: aMoment(d.fecha_fin_evento),
      invitados_por_defecto: d.invitados_por_defecto ?? 1,
      requiere_evaluacion: Boolean(d.requiere_evaluacion),
      requiere_segmentacion: Boolean(d.requiere_segmentacion),
      permite_acompanantes: d.permite_acompanantes === undefined || d.permite_acompanantes === null
        ? true
        : Boolean(d.permite_acompanantes),
      requiere_invitacion: d.requiere_invitacion === undefined || d.requiere_invitacion === null
        ? true
        : Boolean(d.requiere_invitacion),
      respuesta_obligatoria: Boolean(d.respuesta_obligatoria),
      solo_administrativos: Boolean(d.solo_administrativos),
      requiere_pedido: Boolean(d.requiere_pedido),
      requiere_firma: Boolean(d.requiere_firma),
      requiere_qr: Boolean(d.requiere_qr),
      permite_cuotas: Boolean(d.permite_cuotas),
      max_cuotas: d.max_cuotas ?? null,
      recordatorio_horas_antes: d.recordatorio_horas_antes ?? 3,
      preguntas: d.preguntas || [],
      segmentos: d.segmentos || [],
      items: d.items || [],
    });
    setVista("form");
  };

  const setCampo = (campo: string, valor: any) => setForm((f) => ({ ...f, [campo]: valor }));

  // Actualiza la PARTE de fecha de un campo, preservando la hora ya elegida.
  const setFechaParte = (campo: string, m: Moment | null) =>
    setForm((f) => {
      if (!m) return { ...f, [campo]: null };
      const prev = (f as any)[campo] as Moment | null;
      const nuevo = m.clone();
      if (prev) nuevo.hour(prev.hour()).minute(prev.minute());
      nuevo.second(0).millisecond(0);
      return { ...f, [campo]: nuevo };
    });

  // Actualiza la PARTE de hora de un campo, preservando la fecha ya elegida.
  const setHoraParte = (campo: string, m: Moment | null) =>
    setForm((f) => {
      if (!m) return f;
      const prev = (f as any)[campo] as Moment | null;
      const base = prev ? prev.clone() : moment();
      base.hour(m.hour()).minute(m.minute()).second(0).millisecond(0);
      return { ...f, [campo]: base };
    });

  // Preguntas
  const addPregunta = () =>
    setForm((f) => ({
      ...f,
      preguntas: [...f.preguntas, { texto: "", tipo: "OPCION_MULTIPLE", obligatoria: true, opciones: [{ texto: "" }] }],
    }));
  const setPregunta = (i: number, campo: string, valor: any) =>
    setForm((f) => {
      const ps = [...f.preguntas];
      (ps[i] as any)[campo] = valor;
      return { ...f, preguntas: ps };
    });
  const delPregunta = (i: number) =>
    setForm((f) => ({ ...f, preguntas: f.preguntas.filter((_, idx) => idx !== i) }));
  const addOpcion = (i: number) =>
    setForm((f) => {
      const ps = [...f.preguntas];
      ps[i].opciones = [...(ps[i].opciones || []), { texto: "" }];
      return { ...f, preguntas: ps };
    });
  const setOpcion = (i: number, j: number, valor: string) =>
    setForm((f) => {
      const ps = [...f.preguntas];
      const ops = [...(ps[i].opciones || [])];
      ops[j] = { ...ops[j], texto: valor };
      ps[i].opciones = ops;
      return { ...f, preguntas: ps };
    });
  const delOpcion = (i: number, j: number) =>
    setForm((f) => {
      const ps = [...f.preguntas];
      ps[i].opciones = (ps[i].opciones || []).filter((_, idx) => idx !== j);
      return { ...f, preguntas: ps };
    });

  // Segmentos
  const addSegmento = () =>
    setForm((f) => ({ ...f, segmentos: [...f.segmentos, { nombre: "" }] }));
  const setSegmentoNombre = (i: number, nombre: string) =>
    setForm((f) => {
      const ss = [...f.segmentos];
      ss[i] = { ...ss[i], nombre };
      return { ...f, segmentos: ss };
    });
  const delSegmento = (i: number) =>
    setForm((f) => ({ ...f, segmentos: f.segmentos.filter((_, idx) => idx !== i) }));

  // Ítems del catálogo (eventos con pedido)
  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { nombre: "", descripcion: "", precio: 0, cupo: null }] }));
  const setItem = (i: number, campo: string, valor: any) =>
    setForm((f) => {
      const its = [...f.items];
      (its[i] as any)[campo] = valor;
      return { ...f, items: its };
    });
  const delItem = (i: number) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const guardarEvento = async () => {
    if (!form.tema.trim()) {
      present({ message: "El tema es obligatorio", duration: 2500, color: "warning" });
      return;
    }
    if (!form.fecha_inicio_invitacion || !form.fecha_fin_confirmacion || !form.fecha_inicio_evento || !form.fecha_fin_evento) {
      present({ message: "Completá todas las fechas", duration: 2500, color: "warning" });
      return;
    }
    if (form.requiere_pedido) {
      const validos = form.items.filter((it) => it.nombre.trim() && Number(it.precio) > 0);
      if (validos.length === 0) {
        present({ message: "Agregá al menos un ítem con nombre y precio", duration: 3000, color: "warning" });
        return;
      }
      if (form.permite_cuotas && (!form.max_cuotas || form.max_cuotas < 2)) {
        present({ message: "Indicá la cantidad máxima de cuotas (mínimo 2)", duration: 3000, color: "warning" });
        return;
      }
    }
    setGuardando(true);
    try {
      const payload = {
        ...form,
        fecha_inicio_invitacion: aISO(form.fecha_inicio_invitacion),
        fecha_fin_confirmacion: aISO(form.fecha_fin_confirmacion),
        fecha_inicio_evento: aISO(form.fecha_inicio_evento),
        fecha_fin_evento: aISO(form.fecha_fin_evento),
      };
      if (form.id) {
        await Eventos.actualizarEvento(form.id, payload);
        present({ message: "Evento actualizado", duration: 2500, color: "success" });
      } else {
        await Eventos.crearEvento(payload);
        present({ message: "Evento creado (en borrador)", duration: 2500, color: "success" });
      }
      await cargarLista();
      setVista("lista");
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo guardar", duration: 4000, color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  // -------------------- Detalle --------------------
  const abrirDetalle = async (id: number) => {
    const d = await Eventos.obtenerEvento(id);
    setDetalle(d);
    setTabDetalle("invitados");
    setBusqLista("");
    setFiltroSegPres("");
    setInvitados(await Eventos.listarInvitados(id));
    setConfirmados(await Eventos.confirmados(id));
    setRechazados(await Eventos.rechazados(id));
    setPresentes(await Eventos.presentesAdmin(id));
    if (d?.requiere_pedido) {
      try {
        setPedidosInfo(await Eventos.pedidosAdmin(id));
      } catch {
        setPedidosInfo(null);
      }
    } else {
      setPedidosInfo(null);
    }
    setVista("detalle");
  };

  const cambiarEstado = async (estado: "PUBLICADO" | "CANCELADO") => {
    if (!detalle) return;
    const resp = await Eventos.cambiarEstado(detalle.id, estado);
    let msg = `Evento ${estado.toLowerCase()}`;
    if (estado === "PUBLICADO" && resp?.resumen) {
      const r = resp.resumen;
      msg += `. Notificaciones enviadas: ${r.invitaciones_enviadas}`;
    }
    present({ message: msg, duration: 3500, color: "success" });
    await abrirDetalle(detalle.id);
    await cargarLista();
  };

  const procesarNotificaciones = async () => {
    if (!detalle) return;
    try {
      const resp = await Eventos.procesarNotificaciones(detalle.id);
      const r = resp.resumen;
      present({
        message: `Invitaciones: ${r.invitaciones_enviadas} · Recordatorios: ${r.recordatorios_enviados}`,
        duration: 3500,
        color: "success",
      });
      await abrirDetalle(detalle.id);
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo procesar", duration: 4000, color: "danger" });
    }
  };

  // Descarga un Excel con TODOS los presentes del evento (ignora el filtro de pantalla).
  const descargarPresentesExcel = async () => {
    if (!detalle || !presentes.length) return;
    setExportando(true);
    try {
      const conSegmentacion = Boolean(detalle.requiere_segmentacion);
      const conEvaluacion = Boolean(detalle.requiere_evaluacion);
      const conAcompanantes = Boolean(detalle.permite_acompanantes ?? true);

      const columnas: ColumnaExcel[] = [
        { header: "Legajo", key: "legajo" },
        { header: "Cédula", key: "cedula" },
        { header: "Nombre", key: "nombre" },
        { header: "Cargo", key: "cargo" },
        ...(conSegmentacion ? [{ header: "Segmento", key: "segmento" }] : []),
        { header: "Reconocido", key: "reconocido" },
        ...(conAcompanantes ? [{ header: "Acompañantes", key: "acompanantes" }] : []),
        ...(conEvaluacion ? [{ header: "Evaluación", key: "evaluacion" }] : []),
        { header: "Fecha de registro", key: "fecha_registro" },
        { header: "Registrado por", key: "registrado_por" },
      ];

      const filas = presentes.map((p) => ({
        legajo: p.legajo ?? "",
        cedula: p.cedula ?? "",
        nombre: p.nombre || "",
        cargo: p.cargo || "",
        segmento: p.segmento_nombre || "Sin segmento",
        reconocido: p.es_reconocido ? "Sí" : "No",
        acompanantes: p.acompanantes_presentes ?? 0,
        evaluacion: p.evaluacion_completada ? "Completada" : "Pendiente",
        fecha_registro: p.fecha_registro_presente
          ? moment(String(p.fecha_registro_presente).replace(" ", "T")).format("DD/MM/YYYY HH:mm")
          : "",
        registrado_por: p.registrado_por || "",
      }));

      const fecha = moment().format("YYYY-MM-DD");
      await exportarExcel(filas, columnas, `Presentes_${detalle.tema}_${fecha}`, "Presentes");
    } catch (err: any) {
      present({ message: "No se pudo generar el Excel", duration: 3500, color: "danger" });
    } finally {
      setExportando(false);
    }
  };

  // Excel de pedidos/retiros para RRHH: una fila por invitado con pedido.
  // "Total a descontar" solo tiene valor si la persona retiró (política: solo
  // se descuenta lo retirado).
  const descargarPedidosExcel = async () => {
    if (!detalle || !pedidosInfo?.datos?.length) return;
    setExportando(true);
    try {
      const conCuotas = Boolean(detalle.permite_cuotas);
      const columnas: ColumnaExcel[] = [
        { header: "Legajo", key: "legajo" },
        { header: "Cédula", key: "cedula" },
        { header: "Nombre", key: "nombre" },
        { header: "Cargo", key: "cargo" },
        { header: "Pedido", key: "pedido" },
        { header: "Total pedido", key: "total_pedido" },
        { header: "Consentimiento", key: "consentimiento" },
        { header: "Retiró", key: "retirado" },
        { header: "Detalle retirado", key: "detalle_retirado" },
        { header: "Fecha de retiro", key: "fecha_retiro" },
        { header: "Método", key: "metodo" },
        { header: "Firma", key: "firma" },
        { header: "Registrado por", key: "registrado_por" },
        { header: "Total a descontar", key: "total_descontar" },
        ...(conCuotas
          ? [
              { header: "Cuotas", key: "cuotas" },
              { header: "Monto por cuota", key: "monto_cuota" },
            ]
          : []),
      ];
      const filas = pedidosInfo.datos.map((p) => ({
        legajo: p.legajo ?? "",
        cedula: p.cedula ?? "",
        nombre: p.nombre || "",
        cargo: p.cargo || "",
        pedido: (p.items || [])
          .filter((it) => it.cantidad > 0)
          .map((it) => `${it.cantidad}x ${it.nombre}`)
          .join(", "),
        total_pedido: p.total_pedido ?? 0,
        consentimiento: p.consentimiento_fecha
          ? moment(String(p.consentimiento_fecha).replace(" ", "T")).format("DD/MM/YYYY HH:mm")
          : "",
        retirado: p.retirado ? "Sí" : "No",
        detalle_retirado: p.retirado
          ? (p.items || [])
              .filter((it) => (it.cantidad_retirada ?? it.cantidad) > 0)
              .map((it) => `${it.cantidad_retirada ?? it.cantidad}x ${it.nombre}`)
              .join(", ")
          : "",
        fecha_retiro: p.fecha_retiro
          ? moment(String(p.fecha_retiro).replace(" ", "T")).format("DD/MM/YYYY HH:mm")
          : "",
        metodo: p.metodo || "",
        firma: p.tiene_firma ? "Sí" : "No",
        registrado_por: p.registrado_por || "",
        total_descontar: p.retirado ? p.total_retirado ?? p.total_pedido ?? 0 : 0,
        cuotas: p.cuotas ?? 1,
        monto_cuota: p.retirado
          ? Math.round((p.total_retirado ?? p.total_pedido ?? 0) / (p.cuotas || 1))
          : 0,
      }));
      const fecha = moment().format("YYYY-MM-DD");
      await exportarExcel(filas, columnas, `Pedidos_${detalle.tema}_${fecha}`, "Pedidos");
    } catch {
      present({ message: "No se pudo generar el Excel", duration: 3500, color: "danger" });
    } finally {
      setExportando(false);
    }
  };

  // -------------------- Invitados --------------------
  const abrirSelectorInvitados = async () => {
    // Precargar selección con invitados existentes.
    const sel: Record<string, any> = {};
    invitados.forEach((i) => {
      sel[String(i.cedula)] = {
        nombre: i.nombre,
        cargo: i.cargo,
        es_reconocido: Boolean(i.es_reconocido),
        max_acompanantes: i.max_acompanantes ?? 0,
        segmento_id: i.segmento_id ?? null,
      };
    });
    setSeleccion(sel);
    // No precargamos toda la nómina (son muchos): la lista se llena al buscar.
    setUsuarios([]);
    setBusqueda("");
    setShowInvitados(true);
  };

  const buscarUsuarios = async () => {
    const q = busqueda.trim();
    if (!q) {
      setUsuarios([]);
      return;
    }
    const soloAdm = Boolean(detalle?.solo_administrativos ?? form.solo_administrativos);
    setUsuarios(await Eventos.usuariosActivos(q, soloAdm));
  };

  const toggleInvitar = (u: UsuarioActivo) => {
    setSeleccion((prev) => {
      const next = { ...prev };
      if (next[u.cedula]) {
        delete next[u.cedula];
      } else {
        next[u.cedula] = { nombre: u.nombre, cargo: u.cargo, es_reconocido: false, max_acompanantes: 0, segmento_id: null };
      }
      return next;
    });
  };

  const setSegmentoInvitado = (cedula: string, segmentoId: number | null) =>
    setSeleccion((prev) => ({ ...prev, [cedula]: { ...(prev[cedula] || {}), segmento_id: segmentoId } }));

  const toggleReconocido = (cedula: string) => {
    setSeleccion((prev) => {
      const cur = prev[cedula] || {};
      const esRecon = !cur.es_reconocido;
      // Al marcar reconocido, auto-activa el valor por defecto del evento (1).
      const maxAcomp = esRecon
        ? (detalle?.invitados_por_defecto ?? form.invitados_por_defecto ?? 1)
        : cur.max_acompanantes ?? 0;
      return { ...prev, [cedula]: { ...cur, es_reconocido: esRecon, max_acompanantes: maxAcomp } };
    });
  };

  const setMaxAcomp = (cedula: string, valor: number) =>
    setSeleccion((prev) => ({ ...prev, [cedula]: { ...(prev[cedula] || {}), max_acompanantes: valor } }));

  const guardarInvitados = async () => {
    if (!detalle) return;
    const lista = Object.entries(seleccion).map(([cedula, v]) => ({
      cedula,
      nombre: v.nombre,
      cargo: v.cargo,
      es_reconocido: v.es_reconocido,
      max_acompanantes: v.max_acompanantes,
      segmento_id: v.segmento_id ?? null,
    }));
    const res = await Eventos.guardarInvitados(detalle.id, lista);
    const omitidos = Number(res?.omitidos || 0);
    present({
      message: omitidos
        ? `Invitados guardados (${lista.length - omitidos}). ${omitidos} omitido(s) por no ser administrativos.`
        : `Invitados guardados (${lista.length})`,
      duration: omitidos ? 4000 : 2500,
      color: omitidos ? "warning" : "success",
    });
    setShowInvitados(false);
    setInvitados(await Eventos.listarInvitados(detalle.id));
  };

  // -------------------- Tomadores --------------------
  const abrirSelectorTomadores = () => {
    const activos = (detalle?.tomadores || []).filter((t: any) => t.activo).map((t: any) => String(t.cedula));
    const sel: Record<string, boolean> = {};
    activos.forEach((c) => (sel[c] = true));
    setSelTomadores(sel);
    setBusquedaTomadores("");
    setShowTomadores(true);
  };

  const guardarTomadores = async () => {
    if (!detalle) return;
    const lista = invitados
      .filter((i) => selTomadores[String(i.cedula)])
      .map((i) => ({ cedula: i.cedula, nombre: i.nombre }));
    await Eventos.guardarTomadores(detalle.id, lista);
    present({ message: `Anfitriones guardados (${lista.length})`, duration: 2500, color: "success" });
    setShowTomadores(false);
    await abrirDetalle(detalle.id);
  };

  // -------------------- Render --------------------
  if (esAdmin === null || cargando) {
    return (
      <IonPage>
        <Header titulo="Eventos" />
        <IonContent id="eventos-admin">
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!esAdmin) {
    return (
      <IonPage>
        <Header titulo="Eventos" />
        <IonContent id="eventos-admin">
          <div style={{ padding: 24, textAlign: "center" }}>
            <Typography color="textSecondary">No tenés permiso para administrar eventos.</Typography>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo="Eventos" />
      <IonContent id="eventos-admin">
        {vista === "lista" && (
          <div style={{ padding: 12 }}>
            <IonButton expand="block" onClick={nuevoEvento}>+ Nuevo evento</IonButton>
            {eventos.length === 0 && (
              <Typography color="textSecondary" style={{ padding: 16, textAlign: "center" }}>
                No hay eventos creados.
              </Typography>
            )}
            {eventos.map((e) => (
              <div key={e.id} style={{ background: "#fff", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,.15)", padding: 12, marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{e.tema}</Typography>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1565c0" }}>{e.estado}</span>
                </div>
                <Typography variant="body2" color="textSecondary">{e.lugar || "Sin lugar"}</Typography>
                <Typography variant="caption" color="textSecondary">
                  Invitados: {e.total_invitados ?? 0} · Confirmados: {e.total_confirmados ?? 0} · Presentes: {e.total_presentes ?? 0}
                </Typography>
                <div style={{ marginTop: 8 }}>
                  <IonButton size="small" fill="outline" onClick={() => abrirDetalle(e.id)}>Gestionar</IonButton>
                  <IonButton size="small" fill="clear" onClick={() => editarEvento(e.id)}>Editar</IonButton>
                </div>
              </div>
            ))}
          </div>
        )}

        {vista === "form" && (
          <div style={{ padding: 16 }}>
            <IonButton fill="clear" size="small" onClick={() => setVista("lista")}>← Volver</IonButton>
            <Typography variant="h6" style={{ fontWeight: 700, margin: "8px 0" }}>
              {form.id ? "Editar evento" : "Nuevo evento"}
            </Typography>

            <TextField
              label="Tema *"
              variant="outlined"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.tema}
              onChange={(e) => setCampo("tema", e.target.value)}
            />
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                Descripción
              </Typography>
              <IonTextarea
                value={form.descripcion}
                onIonChange={(e) => setCampo("descripcion", e.detail.value || "")}
                autoGrow
                rows={4}
                placeholder="Escribí una descripción del evento..."
                style={{ border: "1px solid #c4c4c4", borderRadius: 4, padding: "4px 10px", "--padding-start": "0px" } as any}
              />
            </div>
            <TextField
              label="Lugar"
              variant="outlined"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.lugar}
              onChange={(e) => setCampo("lugar", e.target.value)}
            />

            <MuiPickersUtilsProvider utils={MomentUtils}>
              {CAMPOS_FECHA.map(({ campo, label }) => (
                <div key={campo} style={{ display: "flex", flexDirection: "column", marginTop: 12 }}>
                  <DatePicker
                    label={label}
                    value={(form as any)[campo] as Moment | null}
                    onChange={(m) => setFechaParte(campo, m)}
                    format="DD/MM/YYYY"
                    inputVariant="outlined"
                    fullWidth
                    okLabel="OK"
                    cancelLabel="Cancelar"
                  />
                  <TimePicker
                    label={`${label} - Hora`}
                    ampm={false}
                    value={(form as any)[campo] as Moment | null}
                    onChange={(m) => setHoraParte(campo, m)}
                    inputVariant="outlined"
                    fullWidth
                    okLabel="OK"
                    cancelLabel="Cancelar"
                    style={{ marginTop: 8 }}
                  />
                </div>
              ))}
            </MuiPickersUtilsProvider>

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">¿Habilitar a todos los funcionarios activos?</IonLabel>
              <IonToggle checked={!form.requiere_invitacion} onIonChange={(e) => setCampo("requiere_invitacion", !e.detail.checked)} />
            </IonItem>

            {!form.requiere_invitacion && !form.solo_administrativos && (
              <Typography variant="caption" color="textSecondary" style={{ display: "block", padding: "0 16px", marginBottom: 4 }}>
                El evento aparecerá en el inicio de TODOS los funcionarios activos y cualquiera podrá
                confirmar{form.requiere_pedido ? " y armar su pedido" : ""} sin estar en una lista de
                invitados (ideal para venta de platos a beneficio). Igual podés precargar invitados si querés.
              </Typography>
            )}

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">Incluir solo administrativos</IonLabel>
              <IonToggle checked={form.solo_administrativos} onIonChange={(e) => setCampo("solo_administrativos", e.detail.checked)} />
            </IonItem>

            {form.solo_administrativos && (
              <Typography variant="caption" color="textSecondary" style={{ display: "block", padding: "0 16px", marginBottom: 4 }}>
                El evento aparece en el inicio de TODO el personal administrativo: mensualeros ADM
                vigentes (excepto la unidad de MANTENIMIENTO) y funcionarios vigentes con cargo de
                fiscalización. No hace falta cargar invitados ni habilitarlo para todos: esta opción
                ya define la audiencia. Al resto no le figura la invitación
                {form.respuesta_obligatoria ? " ni le bloquea recibos / horas" : ""}, y la búsqueda de
                invitados (si querés marcar reconocidos o segmentos) se limita a ese padrón.
              </Typography>
            )}

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">¿Respuesta obligatoria de asistencia?</IonLabel>
              <IonToggle checked={form.respuesta_obligatoria} onIonChange={(e) => setCampo("respuesta_obligatoria", e.detail.checked)} />
            </IonItem>

            {form.respuesta_obligatoria && (
              <Typography variant="caption" color="textSecondary" style={{ display: "block", padding: "0 16px", marginBottom: 4 }}>
                Mientras no respondan si asistirán o no, los invitados no podrán acceder a
                recibos / historial / reclamos de horas (misma regla que la actualización de datos).
                Aplica hasta el fin de la ventana de confirmación.
              </Typography>
            )}

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">¿Permite acompañantes?</IonLabel>
              <IonToggle checked={form.permite_acompanantes} onIonChange={(e) => setCampo("permite_acompanantes", e.detail.checked)} />
            </IonItem>

            {form.permite_acompanantes ? (
              <TextField label="Máximo de acompañantes por invitado" type="number" fullWidth margin="dense" InputProps={{ inputProps: { min: 0 } }} value={form.invitados_por_defecto} onChange={(e) => setCampo("invitados_por_defecto", Math.max(0, Number(e.target.value)))} />
            ) : (
              <Typography variant="caption" color="textSecondary" style={{ display: "block", padding: "0 16px", marginBottom: 4 }}>
                Sin acompañantes: los invitados confirman solo por sí mismos
                {form.requiere_pedido ? " (útil para eventos de venta de platos/productos)" : ""}.
              </Typography>
            )}

            <TextField label="Recordatorio (horas antes, vacío = sin recordatorio)" type="number" fullWidth margin="dense" value={form.recordatorio_horas_antes ?? ""} onChange={(e) => setCampo("recordatorio_horas_antes", e.target.value === "" ? null : Number(e.target.value))} />

            <IonItem lines="none">
              <IonLabel>¿Requiere evaluación post-evento?</IonLabel>
              <IonToggle checked={form.requiere_evaluacion} onIonChange={(e) => setCampo("requiere_evaluacion", e.detail.checked)} />
            </IonItem>

            {form.requiere_evaluacion && (
              <div style={{ marginTop: 8 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Preguntas de evaluación</Typography>
                {form.preguntas.map((p, i) => (
                  <div key={i} style={{ border: "1px solid #eee", borderRadius: 8, padding: 10, marginTop: 8 }}>
                    <TextField label={`Pregunta ${i + 1}`} fullWidth margin="dense" InputLabelProps={{ shrink: true }} value={p.texto} onChange={(e) => setPregunta(i, "texto", e.target.value)} />
                    <TextField
                      select
                      SelectProps={{ native: true }}
                      label="Tipo de respuesta"
                      fullWidth
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      value={p.tipo}
                      onChange={(e) => setPregunta(i, "tipo", e.target.value)}
                    >
                      <option value="OPCION_MULTIPLE">Opción múltiple</option>
                      <option value="ABIERTA">Respuesta abierta</option>
                    </TextField>
                    <IonItem lines="none">
                      <IonLabel>Obligatoria</IonLabel>
                      <IonToggle checked={Boolean(p.obligatoria)} onIonChange={(e) => setPregunta(i, "obligatoria", e.detail.checked)} />
                    </IonItem>
                    {p.tipo === "OPCION_MULTIPLE" && (
                      <div style={{ paddingLeft: 8 }}>
                        {(p.opciones || []).map((o, j) => (
                          <div key={j} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <TextField label={`Opción ${j + 1}`} fullWidth margin="dense" value={o.texto} onChange={(e) => setOpcion(i, j, e.target.value)} />
                            <IonButton
                              size="small"
                              color="danger"
                              fill="clear"
                              onClick={() => delOpcion(i, j)}
                              title="Eliminar opción"
                            >
                              <IonIcon slot="icon-only" icon={trashOutline} />
                            </IonButton>
                          </div>
                        ))}
                        <IonButton size="small" fill="clear" onClick={() => addOpcion(i)}>+ Opción</IonButton>
                      </div>
                    )}
                    <IonButton size="small" color="danger" fill="outline" onClick={() => delPregunta(i)}>Eliminar pregunta</IonButton>
                  </div>
                ))}
                <IonButton size="small" fill="outline" style={{ marginTop: 8 }} onClick={addPregunta}>+ Agregar pregunta</IonButton>
              </div>
            )}

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">¿Requiere segmentación de asistentes?</IonLabel>
              <IonToggle checked={form.requiere_segmentacion} onIonChange={(e) => setCampo("requiere_segmentacion", e.detail.checked)} />
            </IonItem>

            {form.requiere_segmentacion && (
              <div style={{ marginTop: 8 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Segmentos</Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                  El orden define cómo se agrupan los presentes el día del evento (ej. para hacer el reconocimiento por segmento).
                </Typography>
                {form.segmentos.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <TextField label={`Segmento ${i + 1}`} fullWidth margin="dense" value={s.nombre} onChange={(e) => setSegmentoNombre(i, e.target.value)} />
                    <IonButton size="small" color="danger" fill="clear" onClick={() => delSegmento(i)} title="Eliminar segmento">
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    </IonButton>
                  </div>
                ))}
                <IonButton size="small" fill="outline" style={{ marginTop: 8 }} onClick={addSegmento}>+ Agregar segmento</IonButton>
              </div>
            )}

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">¿Pedido de ítems con precio? (platos, productos)</IonLabel>
              <IonToggle checked={form.requiere_pedido} onIonChange={(e) => setCampo("requiere_pedido", e.detail.checked)} />
            </IonItem>

            {form.requiere_pedido && (
              <div style={{ marginTop: 8 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Ítems del evento</Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                  El invitado arma su pedido al confirmar. El total se descuenta del sueldo SOLO si retira el día del evento.
                </Typography>
                {form.items.map((it, i) => (
                  <div key={i} style={{ border: "1px solid #eee", borderRadius: 8, padding: 10, marginTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <TextField label={`Ítem ${i + 1}`} fullWidth margin="dense" InputLabelProps={{ shrink: true }} value={it.nombre} onChange={(e) => setItem(i, "nombre", e.target.value)} />
                      <IonButton size="small" color="danger" fill="clear" onClick={() => delItem(i)} title="Eliminar ítem">
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                    </div>
                    <TextField label="Descripción (opcional)" fullWidth margin="dense" InputLabelProps={{ shrink: true }} value={it.descripcion || ""} onChange={(e) => setItem(i, "descripcion", e.target.value)} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <TextField
                        label="Precio (Gs.)"
                        type="number"
                        fullWidth
                        margin="dense"
                        InputProps={{ inputProps: { min: 0 } }}
                        value={it.precio || ""}
                        onChange={(e) => setItem(i, "precio", Math.max(0, Number(e.target.value)))}
                      />
                      <TextField
                        label="Cupo (vacío = sin límite)"
                        type="number"
                        fullWidth
                        margin="dense"
                        InputProps={{ inputProps: { min: 0 } }}
                        value={it.cupo ?? ""}
                        onChange={(e) => setItem(i, "cupo", e.target.value === "" ? null : Math.max(0, Number(e.target.value)))}
                      />
                    </div>
                  </div>
                ))}
                <IonButton size="small" fill="outline" style={{ marginTop: 8 }} onClick={addItem}>+ Agregar ítem</IonButton>

                <IonItem lines="none">
                  <IonLabel className="ion-text-wrap">¿Permite descuento en cuotas?</IonLabel>
                  <IonToggle checked={form.permite_cuotas} onIonChange={(e) => setCampo("permite_cuotas", e.detail.checked)} />
                </IonItem>

                {form.permite_cuotas && (
                  <>
                    <TextField
                      label="Cantidad máxima de cuotas"
                      type="number"
                      fullWidth
                      margin="dense"
                      InputProps={{ inputProps: { min: 2 } }}
                      value={form.max_cuotas ?? ""}
                      onChange={(e) => setCampo("max_cuotas", e.target.value === "" ? null : Math.max(2, Number(e.target.value)))}
                    />
                    <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                      El invitado elige en cuántas cuotas (1 a {form.max_cuotas ?? "N"}) se le descuenta
                      lo que retire. Se descuenta solo lo efectivamente retirado.
                    </Typography>
                  </>
                )}
              </div>
            )}

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">¿Requiere firma al registrar el retiro/asistencia?</IonLabel>
              <IonToggle checked={form.requiere_firma} onIonChange={(e) => setCampo("requiere_firma", e.detail.checked)} />
            </IonItem>

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">¿Retiro con QR? (el invitado muestra un QR dinámico)</IonLabel>
              <IonToggle checked={form.requiere_qr} onIonChange={(e) => setCampo("requiere_qr", e.detail.checked)} />
            </IonItem>

            {(form.requiere_pedido || form.requiere_firma) && (
              <Typography variant="caption" color="textSecondary" style={{ display: "block", padding: "0 16px" }}>
                El día del evento los anfitriones registran el retiro de cada persona (queda fecha, quién registró
                {form.requiere_firma ? ", firma" : ""}{form.requiere_pedido ? " y total a descontar" : ""}).
              </Typography>
            )}

            <IonButton expand="block" style={{ marginTop: 16 }} disabled={guardando} onClick={guardarEvento}>
              {guardando ? "Guardando..." : "Guardar"}
            </IonButton>
          </div>
        )}

        {vista === "detalle" && detalle && (
          <div style={{ padding: 16 }}>
            <IonButton fill="clear" size="small" onClick={() => { setVista("lista"); cargarLista(); }}>← Volver</IonButton>
            <Typography variant="h6" style={{ fontWeight: 700 }}>{detalle.tema}</Typography>
            <Typography variant="body2" color="textSecondary">{detalle.lugar} · Estado: {detalle.estado}</Typography>
            {detalle.requiere_invitacion !== undefined && !detalle.requiere_invitacion && !detalle.solo_administrativos && (
              <Typography variant="caption" style={{ color: "#1565c0", fontWeight: 700 }}>
                HABILITADO PARA TODOS — los participantes se registran solos al confirmar/pedir
              </Typography>
            )}
            {Boolean(detalle.solo_administrativos) && (
              <Typography variant="caption" style={{ display: "block", color: "#6a1b9a", fontWeight: 700 }}>
                SOLO ADMINISTRATIVOS — visible para todo el padrón (mensualeros ADM sin MANTENIMIENTO + fiscalización) y para nadie más
              </Typography>
            )}

            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
              {detalle.estado === "BORRADOR" && (
                <IonButton size="small" color="success" onClick={() => cambiarEstado("PUBLICADO")}>Publicar</IonButton>
              )}
              {(detalle.estado === "PUBLICADO" || detalle.estado === "EN_CURSO") && (
                <IonButton size="small" color="primary" fill="outline" onClick={procesarNotificaciones}>
                  Enviar notificaciones pendientes
                </IonButton>
              )}
              {detalle.estado !== "CANCELADO" && detalle.estado !== "FINALIZADO" && (
                <IonButton size="small" color="danger" fill="outline" onClick={() => cambiarEstado("CANCELADO")}>Cancelar</IonButton>
              )}
            </div>

            <IonSegment
              scrollable
              value={tabDetalle}
              onIonChange={(e) => { setTabDetalle(e.detail.value as any); setBusqLista(""); }}
              style={{ marginTop: 14 }}
            >
              <IonSegmentButton value="invitados"><IonLabel>Invitados ({invitados.length})</IonLabel></IonSegmentButton>
              <IonSegmentButton value="tomadores"><IonLabel>Anfitriones ({(detalle.tomadores || []).filter((t: any) => t.activo).length})</IonLabel></IonSegmentButton>
              <IonSegmentButton value="confirmados"><IonLabel>Confirmados ({confirmados.length})</IonLabel></IonSegmentButton>
              <IonSegmentButton value="rechazados"><IonLabel>No asisten ({rechazados.length})</IonLabel></IonSegmentButton>
              <IonSegmentButton value="presentes"><IonLabel>Presentes ({presentes.length})</IonLabel></IonSegmentButton>
              {Boolean(detalle.requiere_pedido) && (
                <IonSegmentButton value="pedidos"><IonLabel>Pedidos ({pedidosInfo?.datos?.length ?? 0})</IonLabel></IonSegmentButton>
              )}
            </IonSegment>

            <div style={{ padding: "10px 0 6px" }}>
              <IonInput
                placeholder="Buscar por nombre o cédula"
                value={busqLista}
                onIonChange={(e) => setBusqLista(e.detail.value || "")}
                style={{ border: "1px solid #c4c4c4", borderRadius: 6, paddingLeft: 8 }}
              />
            </div>

            {tabDetalle === "invitados" && (
              <>
                <IonButton size="small" fill="outline" onClick={abrirSelectorInvitados}>Gestionar invitados</IonButton>
                {invitados.filter((i) => coincide(i, busqLista)).map((i) => (
                  <IonItem key={i.id} lines="full">
                    <IonLabel className="ion-text-wrap">
                      <strong>{i.nombre || i.cedula}</strong> — {i.cargo || ""}
                      <div style={{ fontSize: 12, color: "#666" }}>
                        {i.es_reconocido ? "★ Reconocido · " : ""}Acompañantes: {i.max_acompanantes} · {i.estado_confirmacion}
                        {i.presente ? " · PRESENTE" : ""}
                        {i.segmento_nombre ? ` · ${i.segmento_nombre}` : ""}
                      </div>
                    </IonLabel>
                  </IonItem>
                ))}
              </>
            )}

            {tabDetalle === "tomadores" && (
              <>
                <IonButton size="small" fill="outline" onClick={abrirSelectorTomadores} disabled={invitados.length === 0}>
                  Gestionar anfitriones
                </IonButton>
                {(detalle.tomadores || [])
                  .filter((t: any) => t.activo)
                  .filter((t: any) => coincide(t, busqLista))
                  .map((t: any) => (
                    <IonItem key={t.id} lines="full"><IonLabel>{t.nombre || t.cedula}</IonLabel></IonItem>
                  ))}
              </>
            )}

            {tabDetalle === "confirmados" && (
              <>
                {confirmados.filter((c) => coincide(c, busqLista)).map((c, i) => (
                  <IonItem key={i} lines="full">
                    <IonLabel className="ion-text-wrap">
                      {c.nombre || c.cedula}{(c.acompanantes_confirmados ?? 0) > 0 ? ` (+${c.acompanantes_confirmados})` : ""}
                      {c.segmento_nombre ? ` · ${c.segmento_nombre}` : ""}
                    </IonLabel>
                  </IonItem>
                ))}
              </>
            )}

            {tabDetalle === "rechazados" && (
              <>
                {rechazados.filter((r) => coincide(r, busqLista)).map((r, i) => (
                  <IonItem key={i} lines="full">
                    <IonLabel className="ion-text-wrap">
                      <strong>{r.nombre || r.cedula}</strong>
                      {r.cargo ? ` — ${r.cargo}` : ""}
                      <div style={{ fontSize: 12, color: "#666" }}>
                        {r.segmento_nombre ? `${r.segmento_nombre} · ` : ""}
                        Respondió el{" "}
                        {r.fecha_confirmacion
                          ? moment(String(r.fecha_confirmacion).replace(" ", "T")).format("DD/MM/YYYY HH:mm")
                          : "—"}
                      </div>
                    </IonLabel>
                  </IonItem>
                ))}
                {rechazados.length === 0 && (
                  <Typography color="textSecondary" style={{ padding: 16, textAlign: "center" }}>
                    Nadie respondió que no asistirá.
                  </Typography>
                )}
              </>
            )}

            {tabDetalle === "presentes" && (
              <>
                <IonButton
                  size="small"
                  fill="outline"
                  onClick={descargarPresentesExcel}
                  disabled={presentes.length === 0 || exportando}
                  style={{ marginBottom: 8 }}
                >
                  {exportando ? "Generando..." : "⬇ Descargar Excel"}
                </IonButton>
                {Boolean(detalle.requiere_segmentacion) && (detalle.segmentos || []).length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 13 }}>Segmento:</span>
                    <select
                      value={filtroSegPres}
                      onChange={(e) => setFiltroSegPres(e.target.value)}
                      style={{ fontSize: 14, padding: "4px 8px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                    >
                      <option value="">Todos los segmentos</option>
                      {(detalle.segmentos || []).map((s) => (
                        <option key={s.id} value={String(s.id)}>{s.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}
                <EventoPresentesLista
                  presentes={presentes
                    .filter((p) => filtroSegPres === "" || String(p.segmento_id ?? "") === filtroSegPres)
                    .filter(
                      (p) =>
                        coincide(p, busqLista) ||
                        String(p.segmento_nombre || "").toLowerCase().includes(busqLista.trim().toLowerCase())
                    )}
                  requiereEvaluacion={Boolean(detalle.requiere_evaluacion)}
                />
              </>
            )}

            {tabDetalle === "pedidos" && (
              <>
                <IonButton
                  size="small"
                  fill="outline"
                  onClick={descargarPedidosExcel}
                  disabled={!pedidosInfo?.datos?.length || exportando}
                  style={{ marginBottom: 8 }}
                >
                  {exportando ? "Generando..." : "⬇ Descargar Excel"}
                </IonButton>

                {(pedidosInfo?.items_resumen?.length ?? 0) > 0 && (
                  <div style={{ background: "#f5f5f5", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Resumen por ítem</Typography>
                    {pedidosInfo!.items_resumen.map((r, i) => (
                      <Typography key={i} variant="body2">
                        {r.nombre}: {r.pedidas} pedida(s) · {r.retiradas} retirada(s)
                      </Typography>
                    ))}
                    <Typography variant="body2" style={{ fontWeight: 700, marginTop: 6 }}>
                      Total a descontar (solo retirados): {fmtMonto(pedidosInfo!.total_a_descontar)}
                    </Typography>
                  </div>
                )}

                {(pedidosInfo?.datos || []).filter((p) => coincide(p, busqLista)).map((p, i) => (
                  <IonItem key={i} lines="full">
                    <IonLabel className="ion-text-wrap">
                      <strong>{p.nombre || p.cedula}</strong>
                      <div style={{ fontSize: 12, color: "#666" }}>
                        {(p.items || [])
                          .map((it) => {
                            const retirada = it.cantidad_retirada;
                            // cantidad 0 = ítem agregado recién al momento del retiro
                            if (it.cantidad === 0) {
                              return `+${retirada ?? 0}x ${it.nombre} (agregado al retiro)`;
                            }
                            const base = `${it.cantidad}x ${it.nombre}`;
                            return p.retirado && retirada !== null && retirada !== undefined && retirada !== it.cantidad
                              ? `${base} (retiró ${retirada})`
                              : base;
                          })
                          .join(", ")}
                      </div>
                      <div style={{ fontSize: 12 }}>
                        Total: {fmtMonto(p.total_pedido)}
                        {(p.cuotas ?? 1) > 1 ? ` · en ${p.cuotas} cuotas` : ""} ·{" "}
                        {p.retirado ? (
                          <span style={{ color: "#2e7d32", fontWeight: 700 }}>
                            RETIRADO{p.tiene_firma ? " ✍" : ""} ({fmtMonto(p.total_retirado ?? p.total_pedido)})
                          </span>
                        ) : (
                          <span style={{ color: "#b26a00", fontWeight: 700 }}>SIN RETIRAR</span>
                        )}
                      </div>
                    </IonLabel>
                  </IonItem>
                ))}
                {!pedidosInfo?.datos?.length && (
                  <Typography color="textSecondary" style={{ padding: 16, textAlign: "center" }}>
                    Todavía no hay pedidos confirmados.
                  </Typography>
                )}
              </>
            )}
          </div>
        )}

        {/* Modal selector de invitados */}
        <IonModal isOpen={showInvitados} onDidDismiss={() => setShowInvitados(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowInvitados(false)}>Cerrar</IonButton>
              </IonButtons>
              <IonTitle style={{ fontSize: 16 }}>Invitados</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={guardarInvitados}>Guardar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div style={{ padding: 12, display: "flex", gap: 8 }}>
              <IonInput
                placeholder="Buscar por nombre o cédula"
                value={busqueda}
                onIonChange={(e) => setBusqueda(e.detail.value || "")}
                onKeyUp={(e) => { if ((e as any).key === "Enter") buscarUsuarios(); }}
                style={{ border: "1px solid #ddd", borderRadius: 6, flex: 1, paddingLeft: 8 }}
              />
              <IonButton onClick={buscarUsuarios}>Buscar</IonButton>
            </div>
            <Typography variant="caption" color="textSecondary" style={{ padding: "0 12px", display: "block" }}>
              Seleccionados: {Object.keys(seleccion).length}. Buscá por nombre o cédula para agregar invitados.
              {Boolean(detalle?.permite_acompanantes ?? true)
                ? ` Máximo ${detalle?.invitados_por_defecto ?? 1} acompañante(s) por persona.`
                : " Este evento no maneja acompañantes."}
            </Typography>
            {usuarios.map((u) => {
                const sel = seleccion[String(u.cedula)];
              return (
                <div key={u.cedula} style={{ borderBottom: "1px solid #eee", padding: "8px 12px" }}>
                  <IonItem lines="none">
                    <IonCheckbox slot="start" checked={!!sel} onIonChange={() => toggleInvitar(u)} />
                    <IonLabel className="ion-text-wrap">
                      <strong>{u.nombre}</strong>
                      <div style={{ fontSize: 12, color: "#666" }}>CI {u.cedula} · {u.cargo}</div>
                    </IonLabel>
                  </IonItem>
                  {sel && (
                    <div style={{ paddingLeft: 32, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
                        <IonCheckbox checked={!!sel.es_reconocido} onIonChange={() => toggleReconocido(u.cedula)} />
                        Reconocido
                      </label>
                      {Boolean(detalle?.permite_acompanantes ?? true) && (
                        <div style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                          <span>Acompañantes:</span>
                          <select
                            value={sel.max_acompanantes ?? 0}
                            onChange={(e) => setMaxAcomp(u.cedula, Number(e.target.value))}
                            style={{ fontSize: 14, padding: "4px 8px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                          >
                            {Array.from({ length: (detalle?.invitados_por_defecto ?? 0) + 1 }, (_, n) => n).map((n) => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {detalle?.requiere_segmentacion && (detalle?.segmentos || []).length > 0 && (
                        <div style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                          <span>Segmento:</span>
                          <select
                            value={sel.segmento_id ?? ""}
                            onChange={(e) => setSegmentoInvitado(u.cedula, e.target.value === "" ? null : Number(e.target.value))}
                            style={{ fontSize: 14, padding: "4px 8px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                          >
                            <option value="">(sin segmento)</option>
                            {(detalle?.segmentos || []).map((s) => (
                              <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </IonContent>
        </IonModal>

        {/* Modal selector de tomadores de asistencia */}
        <IonModal isOpen={showTomadores} onDidDismiss={() => setShowTomadores(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowTomadores(false)}>Cerrar</IonButton>
              </IonButtons>
              <IonTitle style={{ fontSize: 16 }}>Anfitriones</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={guardarTomadores}>Guardar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <Typography variant="caption" color="textSecondary" style={{ padding: 12, display: "block" }}>
              Elegí entre los invitados quiénes podrán registrar la asistencia el día del evento.
            </Typography>
            <div style={{ padding: "0 12px 8px" }}>
              <IonInput
                placeholder="Buscar por nombre o cédula"
                value={busquedaTomadores}
                onIonChange={(e) => setBusquedaTomadores(e.detail.value || "")}
                style={{ border: "1px solid #c4c4c4", borderRadius: 6, paddingLeft: 8 }}
              />
            </div>
            {invitados
              .filter((i) => {
                const q = busquedaTomadores.trim().toLowerCase();
                if (!q) return true;
                return (
                  String(i.nombre || "").toLowerCase().includes(q) ||
                  String(i.cedula || "").toLowerCase().includes(q)
                );
              })
              .map((i) => (
                <IonItem key={i.id} lines="full">
                  <IonCheckbox
                    slot="start"
                    checked={!!selTomadores[String(i.cedula)]}
                    onIonChange={(e) =>
                      setSelTomadores((prev) => ({ ...prev, [String(i.cedula)]: e.detail.checked }))
                    }
                  />
                  <IonLabel className="ion-text-wrap">
                    <strong>{i.nombre || i.cedula}</strong>
                    <div style={{ fontSize: 12, color: "#666" }}>CI {i.cedula} · {i.cargo || ""}</div>
                  </IonLabel>
                </IonItem>
              ))}
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default EventosAdmin;
